/**
 * Campaign attribution (utm_*) capture.
 *
 * When a buyer lands with utm_* query params we stash them in
 * localStorage with a capture timestamp, so a later course checkout can
 * attribute the purchase to the campaign — even if the buyer comes back
 * in a different session days later. Last-touch within the window: a
 * fresh utm-bearing URL overwrites; a URL with no utm params keeps the
 * earlier capture. Entries older than the attribution window are
 * ignored (and cleared) so stale campaigns don't get undue credit.
 */
const STORAGE_KEY = "utm_params";
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;
// Attribution window — a buyer who landed via an ad has this long to
// purchase before the campaign credit expires.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

interface StoredUtm {
    utm: UtmParams;
    capturedAt: number;
}

/**
 * Read utm_* from the current URL and, if any are present, store them
 * with a capture timestamp. No-op when the URL carries no utm params.
 */
export function captureUtmFromUrl(): void {
    try {
        const params = new URLSearchParams(window.location.search);
        const utm: UtmParams = {};
        for (const k of UTM_KEYS) {
            const v = params.get(`utm_${k}`);
            if (v) utm[k] = v.slice(0, 200);
        }
        if (Object.keys(utm).length > 0) {
            const payload: StoredUtm = { utm, capturedAt: Date.now() };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        }
    } catch {
        // localStorage blocked / unavailable — attribution is best-effort.
    }
}

/** The utm params captured within the attribution window, or null. */
export function getStoredUtm(): UtmParams | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredUtm | null;
        const capturedAt = parsed?.capturedAt;
        const utm = parsed?.utm;
        if (typeof capturedAt !== "number" || !utm || typeof utm !== "object") {
            return null;
        }
        if (Date.now() - capturedAt > TTL_MS) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return utm;
    } catch {
        return null;
    }
}
